import type { FeatureContext } from './featureContext';
import type { EntityId } from '../sim/entity';

/**
 * The Wandering Moongate's client affordances (PRD #362, ADR-0193): the gate and
 * treasure-chest double-clicks plus their one-row long-press menus. Entry is
 * deliberate (double-click / long-press "Enter the moongate"), never walk-over;
 * the server validates reach/level and rules on dead/busy/fate - a transport gate
 * teleports you, a fate gate is the event's to resolve, and an invalid pick
 * fizzles silently.
 */
export function installMoongate(ctx: FeatureContext): void {
  const useGate = (gate: EntityId): void => {
    const playerId = ctx.playerId();
    if (playerId !== undefined) {
      ctx.client()?.sendCommand({ kind: 'EnterMoongate', entity: playerId, gate });
    }
  };

  ctx.onRendererBuilt((renderer) => {
    // Double-clicking a moongate sends an EnterMoongate up the socket - intent only.
    renderer.onMoongateUse = useGate;
    // Double-clicking a treasure chest sends an OpenChest up the socket - intent
    // only (PRD #362 Slice B). The owning event resolves the purse server-side;
    // an unowned chest fizzles silently.
    renderer.onChestUse = (chest) => {
      const playerId = ctx.playerId();
      if (playerId !== undefined) {
        ctx.client()?.sendCommand({ kind: 'OpenChest', entity: playerId, chest });
      }
    };
  });

  // The long-press twins of the double-click entries, contributed as menu
  // *surfaces* (a chest/gate is never a mobile, so they are hit-tested before the
  // character pool and their menu wins the hit). One row each - entering/opening
  // is the whole affordance.
  ctx.menu.addSurface((req) => {
    const chestId = ctx.renderer()?.pickChest(req.cx, req.cy);
    if (chestId === undefined) return null;
    return [
      {
        label: 'Open the chest',
        onSelect: () => {
          const playerId = ctx.playerId();
          if (playerId !== undefined) {
            ctx.client()?.sendCommand({ kind: 'OpenChest', entity: playerId, chest: chestId });
          }
        },
      },
    ];
  });
  ctx.menu.addSurface((req) => {
    const gateId = ctx.renderer()?.pickMoongate(req.cx, req.cy);
    if (gateId === undefined) return null;
    const dungeon = ctx.world()?.get(gateId)?.moongate?.purpose === 'dungeon';
    return [
      {
        label: dungeon ? 'Enter the dungeon' : 'Enter the moongate',
        onSelect: () => useGate(gateId),
      },
    ];
  });
}
